// apps/web/src/features/cargo/numeric.ts
//
// Numeric-like dirty-text handling for the Cargo forms (#1232). The shared
// input schemas keep quantity/money columns (`weight`, `volume`, `costGoods`,
// …, `chinaPaymentAmount`) as nullable "dirty text" STRINGS so a faithful port
// can round-trip whatever the importer wrote verbatim. The FORM validates them
// as numeric (accepting `,` or `.` and surrounding spaces) and normalizes the
// comma to a dot before submit, but submits the SOURCE-COMPATIBLE string — it
// never coerces to a JS number.

// Accepts an optional sign, digits, an optional single `.`/`,` separator, and
// surrounding whitespace. An empty/blank value is "no value" (handled by the
// caller), not an invalid number.
const NUMERIC_LIKE_RE = /^[+-]?\d+([.,]\d+)?$/;

// Money path for `chinaPaymentAmount` (the only field bound to the strict
// `CargoNonnegMoneySchema = ^\d+\.\d{2}$`): NONNEGATIVE (no sign), integer
// digits required, an optional separator with 1 OR 2 fraction digits (rejects
// 3+dp). Kept separate so the other dirty-text columns keep `NUMERIC_LIKE_RE`.
const MONEY_LIKE_RE = /^\d+([.,]\d{1,2})?$/;

/** True when the trimmed value is empty (the field is simply unset). */
export function isBlankCargoNumeric(value: string): boolean {
  return value.trim().length === 0;
}

/**
 * True when a non-blank value parses as a numeric-like token (comma or dot
 * decimal). Blank is considered valid here — required-ness is a separate rule.
 */
export function isValidCargoNumeric(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  return NUMERIC_LIKE_RE.test(trimmed);
}

/**
 * Normalize a numeric-like form value to the source-compatible string the wire
 * expects: trim, swap a decimal comma for a dot. Returns `null` for a blank
 * field so a cleared input maps to the nullable column's `null`.
 */
export function normalizeCargoNumericInput(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return trimmed.replace(',', '.');
}

/**
 * Normalize a FREE-TEXT cargo form value for the wire: trim outer whitespace,
 * blank → `null`. Unlike {@link normalizeCargoNumericInput} it does NOT swap a
 * decimal comma for a dot — the value is stored exactly as typed.
 *
 * WHY (#2141): the transportationTariff / transportationTariffForClients columns
 * accept arbitrary text (currency mixes, tariff combinations, formulas like
 * `5$/kg + 200`), so any numeric normalization would corrupt a legitimate entry.
 */
export function normalizeCargoFreeTextInput(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * True when a non-blank value is a nonnegative money-like token (no sign, 1-2
 * fraction digits). Blank is considered valid here — required-ness is a
 * separate rule (mirrors `isValidCargoNumeric`).
 */
export function isValidCargoMoney(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  return MONEY_LIKE_RE.test(trimmed);
}

/**
 * Normalize a money-like form value to the canonical `\d+.\d{2}` string the
 * `CargoNonnegMoneySchema` wire field (`chinaPaymentAmount`) requires, padding
 * the fraction to 2 digits via STRING ops only — nonnegative, no `Number`
 * round-trip, so long/large integers are preserved exactly. Returns `null` for
 * a blank field, or `undefined` when the value is not money-like (the form's
 * validation surfaces that as a field error first).
 */
export function normalizeCargoMoneyInput(value: string): string | null | undefined {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (!MONEY_LIKE_RE.test(trimmed)) return undefined;
  const [intPart, fracPart = ''] = trimmed.replace(',', '.').split('.');
  return `${intPart}.${fracPart.padEnd(2, '0')}`;
}

/** Mirror the backend dirty-number extraction used by cargo totals and filters. */
export function parseCargoDirtyNumber(value: string | null): number | null {
  if (value === null) return null;
  const cleaned = value.replace(/\s+/gu, '').replace(',', '.').replace(/\+$/, '');
  if (cleaned === '') return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Display-only cargo density in kg/m³, or null when either input is not positive. */
export function formatCargoDensity(weight: string | null, volume: string | null): string | null {
  const parsedWeight = parseCargoDirtyNumber(weight);
  const parsedVolume = parseCargoDirtyNumber(volume);
  if (parsedWeight === null || parsedWeight <= 0 || parsedVolume === null || parsedVolume <= 0) {
    return null;
  }
  return (parsedWeight / parsedVolume).toFixed(1);
}
